import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, faChartLine, faChartBar, faDatabase, 
  faClock, faUsers, faThumbsUp, faChalkboardTeacher, 
  faArrowRight, faStar, faLaptopCode, faFileCsv,
  faChevronDown, faServer, faBook, faQuestionCircle,
  faPenFancy, faUserGraduate, faGraduationCap, faFlask,
  faChartArea, faTable, faCalculator, faProjectDiagram,
  faHeadset, faCog, faFileAlt, faSearch,
  faShieldAlt, faAward, faLightbulb, faClipboardCheck,
  faMoneyBillWave, faChartPie, faCheckDouble
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import Meta from '../components/Meta';
import { Helmet } from 'react-helmet';
import '../styles/ServicePage.scss';
import ServicePageLayout from '../components/ServicePageLayout';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
    }
  })
};

const scaleUp = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.6 } }
};

// SlideUp component for animations
interface SlideUpProps {
  children: React.ReactNode;
  delay?: number;
}

const SlideUp: React.FC<SlideUpProps> = ({ children, delay = 0 }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { 
            duration: 0.8, 
            delay 
          }
        }
      }}
    >
      {children}
    </motion.div>
  );
};

// Testimonial component
interface TestimonialProps {
  name: string;
  role: string;
  text: string;
  rating: number;
}

const Testimonial: React.FC<TestimonialProps> = ({ name, role, text, rating }) => (
  <Card className="testimonial-card h-100">
    <Card.Body>
      <div className="stars mb-3">
        {[...Array(5)].map((_, i) => (
          <FontAwesomeIcon 
            key={i} 
            icon={faStar} 
            className={i < rating ? 'text-warning' : 'text-muted'} 
          />
        ))}
      </div>
      <Card.Text className="testimonial-text">"{text}"</Card.Text>
      <div className="testimonial-author">
        <h6>{name}</h6>
        <small className="text-muted">{role}</small>
      </div>
    </Card.Body>
  </Card>
);

// FAQ item interface
interface FAQItem {
  question: string;
  answer: string;
}

const SPSSAssignmentHelp: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean[]>(new Array(8).fill(false));
  const [imageSrc, setImageSrc] = useState('/images/spss-hero.svg');
  const [stats, setStats] = useState({
    completed: 0,
    students: 0,
    satisfaction: 0,
    tutors: 0
  });

  const featuresRef = useRef<HTMLDivElement>(null);
  const [statsRef, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const controls = useAnimation();

  // FAQ data
  const faqs: FAQItem[] = [
    {
      question: "What types of SPSS assignments do you help with?",
      answer: "We provide comprehensive SPSS help including descriptive statistics, hypothesis testing, regression analysis, ANOVA, chi-square tests, correlation analysis, factor analysis, cluster analysis, and advanced statistical procedures. Our experts handle assignments from basic data entry to complex multivariate analyses."
    },
    {
      question: "Do you provide SPSS syntax along with the analysis?",
      answer: "Yes, we provide complete SPSS syntax files along with detailed output interpretation. This helps you understand the commands used and learn how to perform similar analyses independently. We also include step-by-step instructions for replicating the analysis."
    },
    {
      question: "Can you help with SPSS data cleaning and preparation?",
      answer: "Absolutely! We assist with data cleaning, coding, variable creation, missing data handling, outlier detection, and data transformation. Our experts ensure your dataset is properly prepared for accurate statistical analysis in SPSS."
    },
    {
      question: "What versions of SPSS do you work with?",
      answer: "Our experts are proficient in all recent versions of SPSS, including SPSS Statistics 28, 29, and the latest version. We can work with both Windows and Mac versions, and we're familiar with SPSS syntax and GUI interfaces."
    },
    {
      question: "How do you ensure accuracy in SPSS analysis?",
      answer: "Our SPSS experts follow rigorous quality control procedures, including assumption testing, appropriate statistical method selection, and thorough result verification. Each analysis is reviewed by senior statisticians before delivery to ensure accuracy and reliability."
    },
    {
      question: "Can you help interpret SPSS output for my research?",
      answer: "Yes, we provide detailed interpretation of SPSS output including statistical significance, effect sizes, confidence intervals, and practical implications. Our explanations are tailored to your academic level and research context."
    },
    {
      question: "Do you offer emergency SPSS assignment help?",
      answer: "We offer urgent SPSS assignment help with delivery as fast as 6-12 hours for basic analyses. Complex projects may require more time, but we work to accommodate tight deadlines while maintaining quality standards."
    },
    {
      question: "Is my data secure when using your SPSS service?",
      answer: "Absolutely. We maintain strict confidentiality protocols and secure data handling procedures. Your datasets and research information are protected with encryption and are never shared with third parties. We delete all files after project completion if requested."
    }
  ];

  const toggleAccordion = (index: number) => {
    setIsOpen(prev => prev.map((item, i) => i === index ? !item : item));
  };

  // Handle image loading with fallback
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      console.log('SPSS hero image loaded successfully');
      setImageSrc('/images/spss-hero.svg');
    };
    img.onerror = () => {
      console.error('Error loading SPSS hero image, switching to backup');
      setImageSrc('/images/academic-services.svg'); // Fallback to academic services image
    };
    img.src = '/images/spss-hero.svg';

    // Debug: Check for SVG ID conflicts
    setTimeout(() => {
      const spssElements = document.querySelectorAll('#spss-bg, #spss-shadow, #spss-screenGrad, #spss-chartGrad');
      const conflictElements = document.querySelectorAll('#bg, #shadow, #screenGrad, #chartGrad');
      
      console.log('SPSS SVG elements found:', spssElements.length);
      console.log('Conflicting SVG elements found:', conflictElements.length);
      
      if (conflictElements.length > 0) {
        console.warn('SVG ID conflicts detected! This may cause rendering issues.');
        conflictElements.forEach((el, index) => {
          console.log(`Conflict ${index + 1}:`, el.id, el.tagName);
        });
      }
    }, 1000);
  }, []);

  // Animated counter effect
  useEffect(() => {
    if (inView) {
      const interval = setInterval(() => {
        setStats(prevStats => {
          const newStats = { ...prevStats };
          
          if (newStats.completed < 8500) newStats.completed += Math.ceil((8500 - newStats.completed) / 20);
          if (newStats.students < 3200) newStats.students += Math.ceil((3200 - newStats.students) / 20);
          if (newStats.satisfaction < 98) newStats.satisfaction += Math.ceil((98 - newStats.satisfaction) / 20);
          if (newStats.tutors < 150) newStats.tutors += Math.ceil((150 - newStats.tutors) / 20);
          
          if (
            newStats.completed >= 8500 && 
            newStats.students >= 3200 && 
            newStats.satisfaction >= 98 && 
            newStats.tutors >= 150
          ) {
            clearInterval(interval);
          }
          
          return newStats;
        });
      }, 30);
      
      return () => clearInterval(interval);
    }
  }, [inView]);

  return (
    <ServicePageLayout>
      <div className="service-page spss-assignment-help">
        <Meta
          title="SPSS Assignment Help Service | Expert Statistical Analysis | Homework Guy"
          description="Professional SPSS assignment help from qualified statisticians. Get expert assistance with SPSS data analysis, syntax, interpretation, and statistical procedures. Available 24/7 with guaranteed quality."
          canonicalUrl="https://homeworkguy.org/spss-assignment-help"
        />
        
        <Helmet>
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "Service",
                "name": "SPSS Assignment Help Service",
                "description": "Professional SPSS assignment help from qualified statisticians. Expert assistance with SPSS data analysis, syntax, interpretation, and statistical procedures.",
                "provider": {
                  "@type": "Organization",
                  "name": "Homework Guy",
                  "url": "https://homeworkguy.org/"
                },
                "areaServed": "Worldwide",
                "serviceType": "Statistical Analysis Service",
                "audience": {
                  "@type": "Audience",
                  "audienceType": "Students and Researchers"
                },
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "SPSS Statistical Services",
                  "itemListElement": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "SPSS Data Analysis"
                      }
                    },
                    {
                      "@type": "Offer", 
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Statistical Consultation"
                      }
                    }
                  ]
                }
              }
            `}
          </script>
          
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "What types of SPSS assignments do you help with?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "We provide comprehensive SPSS help including descriptive statistics, hypothesis testing, regression analysis, ANOVA, chi-square tests, correlation analysis, factor analysis, cluster analysis, and advanced statistical procedures. Our experts handle assignments from basic data entry to complex multivariate analyses."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Do you provide SPSS syntax along with the analysis?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, we provide complete SPSS syntax files along with detailed output interpretation. This helps you understand the commands used and learn how to perform similar analyses independently. We also include step-by-step instructions for replicating the analysis."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can you help with SPSS data cleaning and preparation?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Absolutely! We assist with data cleaning, coding, variable creation, missing data handling, outlier detection, and data transformation. Our experts ensure your dataset is properly prepared for accurate statistical analysis in SPSS."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What versions of SPSS do you work with?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Our experts are proficient in all recent versions of SPSS, including SPSS Statistics 28, 29, and the latest version. We can work with both Windows and Mac versions, and we're familiar with SPSS syntax and GUI interfaces."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How do you ensure accuracy in SPSS analysis?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Our SPSS experts follow rigorous quality control procedures, including assumption testing, appropriate statistical method selection, and thorough result verification. Each analysis is reviewed by senior statisticians before delivery to ensure accuracy and reliability."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can you help interpret SPSS output for my research?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, we provide detailed interpretation of SPSS output including statistical significance, effect sizes, confidence intervals, and practical implications. Our explanations are tailored to your academic level and research context."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Do you offer emergency SPSS assignment help?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "We offer urgent SPSS assignment help with delivery as fast as 6-12 hours for basic analyses. Complex projects may require more time, but we work to accommodate tight deadlines while maintaining quality standards."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is my data secure when using your SPSS service?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Absolutely. We maintain strict confidentiality protocols and secure data handling procedures. Your datasets and research information are protected with encryption and are never shared with third parties. We delete all files after project completion if requested."
                    }
                  }
                ]
              }
            `}
          </script>
        </Helmet>
        
        {/* Hero Section */}
        <section className="hero-section">
          <Container>
            <Row className="align-items-center">
              <Col lg={7}>
                <motion.span 
                  className="custom-badge mb-4 d-inline-block"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  EXPERT SPSS STATISTICAL ANALYSIS
                </motion.span>
                
                <motion.h1 
                  className="hero-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  SPSS Assignment <br/>Help Service
                </motion.h1>
                
                <motion.p 
                  className="hero-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Struggling with SPSS statistical analysis? Our qualified experts provide professional help with all types of SPSS assignments, from basic descriptive statistics to advanced multivariate analysis with complete syntax and interpretation.
                </motion.p>
                
                <motion.div 
                  className="hero-features"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="feature">
                    <FontAwesomeIcon icon={faCheckCircle} /> Expert SPSS Analysts
                  </div>
                  <div className="feature">
                    <FontAwesomeIcon icon={faCheckCircle} /> Complete Syntax & Output
                  </div>
                  <div className="feature">
                    <FontAwesomeIcon icon={faCheckCircle} /> 24/7 Support
                  </div>
                </motion.div>
                
                <motion.div 
                  className="hero-cta"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Link to="/order">
                    <Button variant="warning" className="main-cta-btn">
                      Get SPSS Help Now <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  </Link>
                </motion.div>
                
                <motion.div 
                  className="trust-indicators"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <div className="rating">
                    <div className="stars">
                      <FontAwesomeIcon icon={faStar} />
                      <FontAwesomeIcon icon={faStar} />
                      <FontAwesomeIcon icon={faStar} />
                      <FontAwesomeIcon icon={faStar} />
                      <FontAwesomeIcon icon={faStar} />
                    </div>
                    <span>4.9/5.0 • From 1,200+ SPSS Student Reviews</span>
                  </div>
                </motion.div>
              </Col>
              
              <Col lg={5} className="mt-5 mt-lg-0">
                <motion.div 
                  className="hero-image"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <img 
                    src={imageSrc} 
                    alt="help with spss assignment" 
                    className="hero-img"
                    onError={(e) => {
                      console.error('SPSS hero image failed to load, using fallback');
                      e.currentTarget.src = "/images/academic-services.svg";
                    }}
                  />
                  
                  <div className="floating-card card-1">
                    <FontAwesomeIcon icon={faDatabase} /> SPSS Analysis
                  </div>
                  
                  <div className="floating-card card-2">
                    <FontAwesomeIcon icon={faChartLine} /> Statistical Reports
                  </div>
                  
                  <div className="floating-card card-3">
                    <FontAwesomeIcon icon={faCog} /> Data Processing
                  </div>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Stats Section */}
        <section className="stats-section" ref={statsRef}>
          <Container>
            <div className="stats-container">
              <div className="stat-item">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faDatabase} />
                </div>
                <div className="stat-counter">
                  <span className="counter-number">{stats.completed.toLocaleString()}+</span>
                  <span className="stat-label">SPSS Analyses Completed</span>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="stat-counter">
                  <span className="counter-number">{stats.students.toLocaleString()}+</span>
                  <span className="stat-label">Students Helped</span>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faThumbsUp} />
                </div>
                <div className="stat-counter">
                  <span className="counter-number">{stats.satisfaction}%</span>
                  <span className="stat-label">Satisfaction Rate</span>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <div className="stat-counter">
                  <span className="counter-number">{stats.tutors}+</span>
                  <span className="stat-label">SPSS Experts</span>
                </div>
              </div>
            </div>
          </Container>
        </section>
        
        {/* Services Overview */}
        <section className="services-overview-section">
          <Container>
            <div className="section-header text-center">
              <SlideUp>
                <span className="section-badge bg-primary-light text-primary">OUR SERVICES</span>
                <h2>SPSS Assignment Services We Provide</h2>
                <p className="section-subtitle">
                  Comprehensive solutions for all your SPSS statistical needs
                </p>
              </SlideUp>
            </div>

            <Row className="mt-5">
              <Col lg={4} md={6} className="mb-4">
                <SlideUp delay={0.1}>
                  <div className="service-type-card">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faChartBar} />
                    </div>
                    <h4>Descriptive Statistics</h4>
                    <p>Expert help with frequency analysis, measures of central tendency, variability, and data visualization using SPSS charts and graphs.</p>
                  </div>
                </SlideUp>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <SlideUp delay={0.2}>
                  <div className="service-type-card">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faCalculator} />
                    </div>
                    <h4>Hypothesis Testing</h4>
                    <p>Professional assistance with t-tests, ANOVA, chi-square tests, and non-parametric tests with proper assumption checking and interpretation.</p>
                  </div>
                </SlideUp>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <SlideUp delay={0.3}>
                  <div className="service-type-card">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <h4>Regression Analysis</h4>
                    <p>Expert support with linear, multiple, logistic, and advanced regression models including model building and diagnostic procedures.</p>
                  </div>
                </SlideUp>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <SlideUp delay={0.4}>
                  <div className="service-type-card">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faProjectDiagram} />
                    </div>
                    <h4>Factor Analysis</h4>
                    <p>Professional help with exploratory and confirmatory factor analysis, principal component analysis, and scale reliability testing.</p>
                  </div>
                </SlideUp>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <SlideUp delay={0.5}>
                  <div className="service-type-card">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faDatabase} />
                    </div>
                    <h4>Data Management</h4>
                    <p>Comprehensive assistance with data cleaning, recoding, transformation, missing data handling, and dataset preparation.</p>
                  </div>
                </SlideUp>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <SlideUp delay={0.6}>
                  <div className="service-type-card">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faChartArea} />
                    </div>
                    <h4>Advanced Analytics</h4>
                    <p>Expert help with cluster analysis, discriminant analysis, time series analysis, and other advanced SPSS procedures.</p>
                  </div>
                </SlideUp>
              </Col>
            </Row>
          </Container>
        </section>
        
        {/* Features Section */}
        <section className="features-section">
          <Container>
            <SlideUp>
              <div className="section-header text-center mb-5">
                <span className="section-badge bg-primary-light text-primary mb-3 d-inline-block">
                  WHY CHOOSE US
                </span>
                <h2 className="section-title">
                  What Makes Our SPSS Help Different
                </h2>
                <p className="section-description">
                  We combine SPSS expertise, statistical knowledge, and clear explanations
                </p>
              </div>
            </SlideUp>

            <Row className="g-4">
              <Col md={6} lg={4}>
                <SlideUp delay={0.1}>
                  <div className="feature-card">
                    <div className="feature-icon-container">
                      <FontAwesomeIcon icon={faUserGraduate} className="feature-icon" />
                    </div>
                    <h3>SPSS Certified Experts</h3>
                    <p>Our team consists of statisticians and data analysts with advanced SPSS certifications and years of experience in statistical software.</p>
                  </div>
                </SlideUp>
              </Col>
              <Col md={6} lg={4}>
                <SlideUp delay={0.2}>
                  <div className="feature-card">
                    <div className="feature-icon-container">
                      <FontAwesomeIcon icon={faLaptopCode} className="feature-icon" />
                    </div>
                    <h3>Complete Syntax & Output</h3>
                    <p>We provide comprehensive SPSS syntax files, detailed output files, and step-by-step instructions for result interpretation.</p>
                  </div>
                </SlideUp>
              </Col>
              <Col md={6} lg={4}>
                <SlideUp delay={0.3}>
                  <div className="feature-card">
                    <div className="feature-icon-container">
                      <FontAwesomeIcon icon={faBook} className="feature-icon" />
                    </div>
                    <h3>Educational Approach</h3>
                    <p>Our solutions include detailed explanations of statistical concepts, SPSS procedures, and practical insights for learning.</p>
                  </div>
                </SlideUp>
              </Col>
              <Col md={6} lg={4}>
                <SlideUp delay={0.4}>
                  <div className="feature-card">
                    <div className="feature-icon-container">
                      <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
                    </div>
                    <h3>Data Security</h3>
                    <p>We maintain strict confidentiality protocols and secure data handling procedures to protect your research information.</p>
                  </div>
                </SlideUp>
              </Col>
              <Col md={6} lg={4}>
                <SlideUp delay={0.5}>
                  <div className="feature-card">
                    <div className="feature-icon-container">
                      <FontAwesomeIcon icon={faHeadset} className="feature-icon" />
                    </div>
                    <h3>24/7 Support</h3>
                    <p>Our team is available around the clock to answer questions and provide updates on your SPSS assignment progress.</p>
                  </div>
                </SlideUp>
              </Col>
              <Col md={6} lg={4}>
                <SlideUp delay={0.6}>
                  <div className="feature-card">
                    <div className="feature-icon-container">
                      <FontAwesomeIcon icon={faAward} className="feature-icon" />
                    </div>
                    <h3>Quality Assurance</h3>
                    <p>Each SPSS analysis undergoes rigorous quality checks and peer review to ensure accuracy and statistical validity.</p>
                  </div>
                </SlideUp>
              </Col>
            </Row>
          </Container>
        </section>
        
        {/* How It Works */}
        <section className="how-it-works-section">
          <Container>
            <div className="section-header text-center">
              <SlideUp>
                <motion.span 
                  className="enhanced-section-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  THE PROCESS
                </motion.span>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  How Our SPSS Assignment Help Works
                </motion.h2>
                <motion.p 
                  className="section-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  Get expert SPSS help in just a few simple steps
                </motion.p>
              </SlideUp>
            </div>

            <Row className="process-steps mt-5">
              <Col lg={3} md={6} className="mb-4">
                <SlideUp delay={0.1}>
                  <motion.div 
                    className="enhanced-process-card"
                    whileHover={{ 
                      y: -10, 
                      boxShadow: '0 20px 40px rgba(0, 123, 255, 0.15)' 
                    }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="step-number-enhanced">
                      <span className="number">1</span>
                      <div className="step-icon">
                        <FontAwesomeIcon icon={faClipboardCheck} />
                      </div>
                    </div>
                    <h3>Submit Assignment</h3>
                    <p>Share your SPSS assignment requirements, dataset, and specific analysis needs with our team.</p>
                  </motion.div>
                </SlideUp>
              </Col>
              <Col lg={3} md={6} className="mb-4">
                <SlideUp delay={0.2}>
                  <motion.div 
                    className="enhanced-process-card"
                    whileHover={{ 
                      y: -10, 
                      boxShadow: '0 20px 40px rgba(0, 123, 255, 0.15)' 
                    }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="step-number-enhanced">
                      <span className="number">2</span>
                      <div className="step-icon">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                      </div>
                    </div>
                    <h3>Get a Quote</h3>
                    <p>Receive a custom price quote based on the complexity and timeline of your SPSS analysis project.</p>
                  </motion.div>
                </SlideUp>
              </Col>
              <Col lg={3} md={6} className="mb-4">
                <SlideUp delay={0.3}>
                  <motion.div 
                    className="enhanced-process-card"
                    whileHover={{ 
                      y: -10, 
                      boxShadow: '0 20px 40px rgba(0, 123, 255, 0.15)' 
                    }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="step-number-enhanced">
                      <span className="number">3</span>
                      <div className="step-icon">
                        <FontAwesomeIcon icon={faChartPie} />
                      </div>
                    </div>
                    <h3>Expert Analysis</h3>
                    <p>Our SPSS experts perform the statistical analysis using appropriate methods and generate comprehensive output.</p>
                  </motion.div>
                </SlideUp>
              </Col>
              <Col lg={3} md={6} className="mb-4">
                <SlideUp delay={0.4}>
                  <motion.div 
                    className="enhanced-process-card"
                    whileHover={{ 
                      y: -10, 
                      boxShadow: '0 20px 40px rgba(0, 123, 255, 0.15)' 
                    }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="step-number-enhanced">
                      <span className="number">4</span>
                      <div className="step-icon">
                        <FontAwesomeIcon icon={faCheckDouble} />
                      </div>
                    </div>
                    <h3>Review & Delivery</h3>
                    <p>Receive your complete SPSS solution with syntax, output, interpretation, and detailed explanations.</p>
                  </motion.div>
                </SlideUp>
              </Col>
            </Row>

            <motion.div 
              className="text-center mt-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <Link to="/order">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Button variant="warning" size="lg" className="enhanced-main-cta-btn">
                    <span>Get SPSS Help Now</span>
                    <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                  </Button>
                </motion.div>
              </Link>
              <motion.p 
                className="cta-subtitle"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                viewport={{ once: true }}
              >
                ✓ Free consultation • ✓ 24/7 support • ✓ Money-back guarantee
              </motion.p>
            </motion.div>
          </Container>
        </section>
        
        {/* Other Services Section */}
        <section className="other-services-section py-5 bg-light">
          <Container>
            <div className="text-center mb-5">
              <h2>Other Academic Services You Will Like</h2>
            </div>
            <Row className="justify-content-center text-center">
              <Col lg={3} md={6} className="mb-3">
                <a href="/statistics-assignment-help-service" className="text-decoration-none text-secondary service-link">statistics assignment help service</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/math-assignment-help-service" className="text-decoration-none text-secondary service-link">advanced math assignment help</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/research-paper-writing-service" className="text-decoration-none text-secondary service-link">research methodology help</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/database-management-service" className="text-decoration-none text-secondary service-link">data analysis help service</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/programming-assignment-help-services" className="text-decoration-none text-secondary service-link">R programming help</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/online-class-help-services" className="text-decoration-none text-secondary service-link">statistics class help online</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/homework-help-service" className="text-decoration-none text-secondary service-link">statistical homework help</a>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <a href="/hire-someone-to-take-my-statistics-class" className="text-decoration-none text-secondary service-link">take my statistics class</a>
              </Col>
            </Row>
            <style>
              {`
                .service-link:hover {
                  color: #007bff !important;
                  text-decoration: none;
                  transition: color 0.2s ease;
                }
              `}
            </style>
          </Container>
        </section>
        
        {/* Testimonials Section */}
        <section className="testimonials-section">
          <Container>
            <div className="section-header text-center">
              <SlideUp>
                <div className="premium-badge">
                  <span className="section-badge bg-primary-light text-primary">
                    <FontAwesomeIcon icon={faStar} className="me-2" />
                    TESTIMONIALS
                  </span>
                </div>
                <h2>What Our SPSS Students Say</h2>
                <p className="section-subtitle">
                  Read success stories from students who achieved excellent results with our SPSS help
                </p>
              </SlideUp>
            </div>
            
            <Row className="mt-5">
              <Col md={4} className="mb-4">
                <SlideUp delay={0.1}>
                  <motion.div 
                    className="testimonial-card-enhanced"
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="testimonial-header">
                      <div className="stars-rating">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon key={i} icon={faStar} className="star-icon" />
                        ))}
                      </div>
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faThumbsUp} />
                      </div>
                    </div>
                    
                    <div className="testimonial-content">
                      <p className="testimonial-text">
                        "The SPSS help I received was outstanding! They provided complete syntax files and helped me understand factor analysis for my thesis. The detailed explanations helped me learn SPSS properly."
                      </p>
                    </div>
                    
                    <div className="testimonial-footer">
                      <div className="author-info">
                        <h5 className="author-name">Emily R.</h5>
                        <p className="author-role">Psychology Graduate Student</p>
                      </div>
                      <div className="verified-badge">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>Verified</span>
                      </div>
                    </div>
                  </motion.div>
                </SlideUp>
              </Col>
              
              <Col md={4} className="mb-4">
                <SlideUp delay={0.2}>
                  <motion.div 
                    className="testimonial-card-enhanced"
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="testimonial-header">
                      <div className="stars-rating">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon key={i} icon={faStar} className="star-icon" />
                        ))}
                      </div>
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faThumbsUp} />
                      </div>
                    </div>
                    
                    <div className="testimonial-content">
                      <p className="testimonial-text">
                        "Struggling with regression analysis in SPSS, but their expert made it so clear. Got step-by-step syntax, perfect output interpretation, and learned how to apply it to my research project."
                      </p>
                    </div>
                    
                    <div className="testimonial-footer">
                      <div className="author-info">
                        <h5 className="author-name">Marcus T.</h5>
                        <p className="author-role">Business Analytics Student</p>
                      </div>
                      <div className="verified-badge">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>Verified</span>
                      </div>
                    </div>
                  </motion.div>
                </SlideUp>
              </Col>
              
              <Col md={4} className="mb-4">
                <SlideUp delay={0.3}>
                  <motion.div 
                    className="testimonial-card-enhanced"
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="testimonial-header">
                      <div className="stars-rating">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon key={i} icon={faStar} className="star-icon" />
                        ))}
                      </div>
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faThumbsUp} />
                      </div>
                    </div>
                    
                    <div className="testimonial-content">
                      <p className="testimonial-text">
                        "Excellent SPSS assistance for my dissertation analysis. They handled complex ANOVA designs and provided professional-quality output with clear interpretation. Highly recommend their service!"
                      </p>
                    </div>
                    
                    <div className="testimonial-footer">
                      <div className="author-info">
                        <h5 className="author-name">Sarah K.</h5>
                        <p className="author-role">Health Sciences Researcher</p>
                      </div>
                      <div className="verified-badge">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>Verified</span>
                      </div>
                    </div>
                  </motion.div>
                </SlideUp>
              </Col>
            </Row>
          </Container>
        </section>
        {/* SEO Content Section */}
        <section className="seo-content-section py-4 bg-light">
          <Container>
            <div className="section-header text-center mb-4">
              <SlideUp>
                <span className="section-badge bg-primary-light text-primary">
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  ACADEMIC RESOURCES
                </span>
                <h2>Expert SPSS Assignment Help for Academic Success</h2>
                <p className="section-subtitle">
                  Comprehensive information about our professional SPSS statistical analysis services
                </p>
              </SlideUp>
            </div>
            <div className="seo-content-wrapper">
              <Row className="justify-content-center mb-4">
                <Col lg={10}>
                  <p className="mb-4">
                    When you need reliable help with SPSS assignment challenges, our team of qualified statisticians and data analysis experts provides comprehensive support for students at all academic levels. From undergraduate research projects to doctoral dissertations, we deliver professional SPSS assignment help that combines technical expertise with clear explanations to enhance your statistical learning experience.
                  </p>
                  
                  <p className="mb-4">
                    Our <a href="/statistics-assignment-help-service" className="text-decoration-none">Statistics Assignment Help Service</a> specializes in SPSS software applications across diverse statistical procedures. Whether you're struggling with basic data entry and cleaning or need assistance with complex multivariate analyses, our certified SPSS experts understand the software's capabilities and limitations to provide accurate, meaningful results for your academic projects.
                  </p>
                  
                  <h3 className="mb-3">Comprehensive SPSS Assignment Support Services</h3>
                  <p className="mb-4">
                    Students seeking spss assignment help often face challenges with data preparation, statistical methodology selection, and result interpretation. Our service addresses these common pain points through systematic approach that begins with thorough requirement analysis and extends through complete documentation delivery. We handle assignments ranging from simple descriptive statistics to advanced econometric modeling, ensuring every project meets academic standards while building your statistical competency.
                  </p>
                  
                  <Row className="mb-5">
                    <Col md={6}>
                      <div className="seo-content-card h-100">
                        <h4 className="mb-3">Data Analysis Expertise</h4>
                        <ul className="list-unstyled">
                          <li className="mb-2">✓ Descriptive statistics and frequency analysis</li>
                          <li className="mb-2">✓ Hypothesis testing and inferential statistics</li>
                          <li className="mb-2">✓ Regression analysis (linear, multiple, logistic)</li>
                          <li className="mb-2">✓ ANOVA and ANCOVA procedures</li>
                          <li className="mb-2">✓ Chi-square tests and non-parametric analysis</li>
                          <li className="mb-2">✓ Factor analysis and principal components</li>
                          <li className="mb-2">✓ Cluster analysis and discriminant function</li>
                          <li className="mb-2">✓ Time series analysis and forecasting</li>
                        </ul>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="seo-content-card h-100">
                        <h4 className="mb-3">Technical Support Areas</h4>
                        <ul className="list-unstyled">
                          <li className="mb-2">✓ Data cleaning and preparation</li>
                          <li className="mb-2">✓ Variable creation and recoding</li>
                          <li className="mb-2">✓ Missing data handling strategies</li>
                          <li className="mb-2">✓ Syntax writing and automation</li>
                          <li className="mb-2">✓ Output interpretation and reporting</li>
                          <li className="mb-2">✓ Chart and graph creation</li>
                          <li className="mb-2">✓ Database integration and management</li>
                          <li className="mb-2">✓ Research methodology consultation</li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                  
                  <h3 className="mb-3">Why Students Choose Our SPSS Assignment Help</h3>
                  <p className="mb-4">
                    Professional help with spss assignment projects requires more than technical knowledge, it demands understanding of statistical theory, research methodology, and academic writing standards. Our interdisciplinary team combines advanced degrees in statistics, psychology, economics, and social sciences with practical SPSS experience across various research contexts. This expertise enables us to provide contextually appropriate solutions that align with your specific academic discipline and assignment requirements.
                  </p>
                  
                  <p className="mb-4">
                    Unlike generic tutoring services, our <Link to="/math-assignment-help-service" className="text-decoration-none">Math Assignment Help Service</Link> includes specialized SPSS support that recognizes the software's role in modern statistical education. We provide complete syntax files, detailed output explanations, and step-by-step methodology documentation that serves as valuable learning resources for future assignments. This educational approach ensures you gain practical skills while achieving excellent grades on current projects.
                  </p>
                  
                  <div className="seo-content-section">
                    <h3 className="mb-4">Professional SPSS Assignment Help: Complete Statistical Analysis Support</h3>
                    
                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <div className="seo-content-card h-100">
                          <h4 className="mb-3">Expert SPSS Data Analysis</h4>
                          <p className="mb-3">
                            Our SPSS assignment help covers all statistical procedures including descriptive statistics, t-tests, ANOVA, regression analysis, correlation studies, and advanced multivariate techniques. Expert statisticians ensure accurate syntax, proper interpretation, and comprehensive reporting that meets academic standards.
                          </p>
                          <ul className="mb-0">
                            <li>Descriptive and inferential statistics</li>
                            <li>Hypothesis testing and significance analysis</li>
                            <li>Data visualization and chart creation</li>
                            <li>Statistical interpretation and reporting</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-4">
                        <div className="seo-content-card h-100">
                          <h4 className="mb-3">Academic Integration Support</h4>
                          <p className="mb-3">
                            Students frequently need assistance integrating SPSS results with written reports, presentations, and research papers. Our service includes guidance on statistical reporting standards, APA format requirements, and effective data visualization techniques that enhance your overall academic performance.
                          </p>
                          <ul className="mb-0">
                            <li>APA format statistical reporting</li>
                            <li>Research methodology guidance</li>
                            <li>Academic writing coordination</li>
                            <li>Presentation and visualization support</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <div className="seo-content-card h-100">
                          <h4 className="mb-3">Advanced Statistical Procedures</h4>
                          <p className="mb-3">
                            Our SPSS experts specialize in complex statistical analysis including factor analysis, cluster analysis, discriminant analysis, time series analysis, and non-parametric tests. We handle sophisticated research designs and provide comprehensive interpretation for graduate-level projects.
                          </p>
                          <ul className="mb-0">
                            <li>Multivariate statistical analysis</li>
                            <li>Factor and cluster analysis</li>
                            <li>Time series and forecasting</li>
                            <li>Non-parametric statistical tests</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-4">
                        <div className="seo-content-card h-100">
                          <h4 className="mb-3">Quality Assurance & Academic Integrity</h4>
                          <p className="mb-3">
                            When seeking help with SPSS assignment work, students rightfully expect solutions that maintain academic integrity while providing genuine learning value. Our quality assurance process includes multiple verification stages and educational documentation.
                          </p>
                          <ul className="mb-0">
                            <li>Statistical methodology review</li>
                            <li>Syntax validation and testing</li>
                            <li>Output accuracy verification</li>
                            <li>Original work with proper citations</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <div className="seo-content-card h-100">
                          <h4 className="mb-3">Data Management & Preparation</h4>
                          <p className="mb-3">
                            Proper data management forms the foundation of accurate statistical analysis. Our SPSS assignment help includes comprehensive data cleaning, variable coding, missing value treatment, and database preparation to ensure reliable analytical results.
                          </p>
                          <ul className="mb-0">
                            <li>Data cleaning and validation</li>
                            <li>Variable transformation and coding</li>
                            <li>Missing data treatment strategies</li>
                            <li>Database merging and restructuring</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-4">
                        <div className="seo-content-card h-100">
                          <h4 className="mb-3">Comprehensive Learning Support</h4>
                          <p className="mb-3">
                            Our SPSS assignment help service extends beyond task completion to include comprehensive learning support that builds your statistical competency. We provide detailed methodology documentation and step-by-step explanations for future reference.
                          </p>
                          <ul className="mb-0">
                            <li>Complete syntax files with comments</li>
                            <li>Detailed output explanations</li>
                            <li>Statistical theory foundations</li>
                            <li>Ongoing academic support</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="seo-highlight-section mt-5">
                      <div className="text-center mb-4">
                        <h4 className="mb-3">Why Choose Our Professional SPSS Assignment Help?</h4>
                        <p className="lead mb-4">Trusted by thousands of students worldwide for accurate, timely, and educational SPSS assignment solutions</p>
                      </div>
                      
                      <div className="row">
                        <div className="col-md-4 mb-4">
                          <div className="feature-highlight text-center">
                            <div className="feature-icon mb-3">
                              <FontAwesomeIcon icon={faUserGraduate} className="fa-2x text-primary" />
                            </div>
                            <h5 className="mb-3">Expert Statisticians</h5>
                            <p>Advanced degrees in statistics, psychology, economics, and social sciences with practical SPSS experience across various research contexts and academic disciplines.</p>
                          </div>
                        </div>
                        <div className="col-md-4 mb-4">
                          <div className="feature-highlight text-center">
                            <div className="feature-icon mb-3">
                              <FontAwesomeIcon icon={faCheckDouble} className="fa-2x text-primary" />
                            </div>
                            <h5 className="mb-3">Complete Solutions</h5>
                            <p>Full statistical analysis including data preparation, analysis execution, interpretation, and academic formatting for immediate submission and excellent grades.</p>
                          </div>
                        </div>
                        <div className="col-md-4 mb-4">
                          <div className="feature-highlight text-center">
                            <div className="feature-icon mb-3">
                              <FontAwesomeIcon icon={faLightbulb} className="fa-2x text-primary" />
                            </div>
                            <h5 className="mb-3">Learning Resources</h5>
                            <p>Detailed documentation, syntax explanations, and methodology guides that serve as valuable learning materials for future assignments and statistical competency building.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="row mt-4">
                        <div className="col-md-4 mb-3">
                          <div className="feature-highlight text-center">
                            <div className="feature-icon mb-3">
                              <FontAwesomeIcon icon={faClock} className="fa-2x text-primary" />
                            </div>
                            <h5 className="mb-3">24/7 Support</h5>
                            <p>Round-the-clock assistance for urgent assignments and ongoing statistical project support with flexible delivery schedules.</p>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="feature-highlight text-center">
                            <div className="feature-icon mb-3">
                              <FontAwesomeIcon icon={faShieldAlt} className="fa-2x text-primary" />
                            </div>
                            <h5 className="mb-3">Quality Guarantee</h5>
                            <p>Rigorous quality control processes ensure accurate statistical analysis and academic integrity in all delivered solutions.</p>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="feature-highlight text-center">
                            <div className="feature-icon mb-3">
                              <FontAwesomeIcon icon={faMoneyBillWave} className="fa-2x text-primary" />
                            </div>
                            <h5 className="mb-3">Affordable Pricing</h5>
                            <p>Competitive rates with transparent pricing structure designed to fit student budgets without compromising solution quality.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
        
        {/* FAQ Section */}
        <section className="faq-section py-5">
          <Container>
            <div className="section-header text-center">
              <SlideUp>
                <span className="section-badge bg-primary-light text-primary">FAQ</span>
                <h2>Frequently Asked Questions</h2>
                <p className="section-subtitle">
                  Get answers to common questions about our SPSS assignment help service
                </p>
              </SlideUp>
            </div>
            
            <div className="accordion-container">
              {faqs.map((faq, index) => (
                <motion.div 
                  key={index} 
                  className="accordion-item"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={fadeIn}
                  custom={index * 0.1}
                >
                  <div 
                    className={`accordion-header ${isOpen[index] ? 'active' : ''}`} 
                    onClick={() => toggleAccordion(index)}
                  >
                    <h4>{faq.question}</h4>
                    <span className="toggle-icon">
                      <FontAwesomeIcon icon={faChevronDown} />
                    </span>
                  </div>
                  <AnimatePresence>
                    {isOpen[index] && (
                      <motion.div 
                        className="accordion-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <p>{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default SPSSAssignmentHelp; 